import React, { useCallback, useMemo, useState } from 'react';
import { useInsightsUtils } from '../../../../../hooks/useInsightsUtils';
import { formatUTCDateTime } from '../../../../../utils/formatUTCDate';
import styles from './lockUnlockHeader.module.css';
import { IDENTITIES } from './lockUnlockHeader.constants';
import { getShouldRenderLockUnlockHeader, isSsnUnlocked } from '../../../../../utils/insightApplicationPage';

interface UseLockUnlockHeaderProps {
    currentAppDetails: Record<string, any>;
}

export const useLockUnlockHeader = ({
    currentAppDetails,
}: UseLockUnlockHeaderProps) => {
    const { UnLockApplication, UnLockSsnApplication } = useInsightsUtils();

    const [isUnlockModalOpen, setIsUnlockModalOpen] = useState(false);
    const [isSsnUnlockModalOpen, setIsSsnUnlockModalOpen] = useState(false);

    const isApplicationLocked = Boolean(currentAppDetails.isLocked);
    const isApplicationUnlocked = Boolean(currentAppDetails.isManuallyUnlocked);
    const isSsnLocked = Boolean(currentAppDetails.isSsnLocked);
    const isSsnUnlockedFlag = isSsnUnlocked(currentAppDetails);

    const isUnlockedThroughSsn =
        Boolean(currentAppDetails.isUnlockedThroughSsn) &&
        Boolean(currentAppDetails.benefitedSsnApplicationDetails?.lockedOpportunity);

    const isAnyLocked = isApplicationLocked || isSsnLocked;
    const isAnyUnlocked = isApplicationUnlocked || isSsnUnlockedFlag;

    const shouldRenderHeader = getShouldRenderLockUnlockHeader(currentAppDetails);

    const unlockedBy = isSsnUnlockedFlag
        ? currentAppDetails.selfSsnLockedDetails?.unlockedBy
        : currentAppDetails.unlockedBy;

    const unlockedAt = useMemo(() => {
        if (isSsnUnlockedFlag) {
            const unlockedOn = currentAppDetails.selfSsnLockedDetails?.unlockedOn;
            return unlockedOn ? formatUTCDateTime(unlockedOn, false, true) : '-';
        }

        if (currentAppDetails.unlockedOn) {
            return formatUTCDateTime(currentAppDetails.unlockedOn, false);
        }

        return '-';
    }, [
        isSsnUnlockedFlag,
        currentAppDetails.selfSsnLockedDetails?.unlockedOn,
        currentAppDetails.unlockedOn,
    ])

    const beneficiaryOpportunityId = isSsnUnlockedFlag ? currentAppDetails.selfSsnLockedDetails?.benefitedOpportunityId : null;
    const beneficiaryOpportunityUrl = beneficiaryOpportunityId ? `/insights/moreInfo?opportunityId=${beneficiaryOpportunityId}` : null;

    const remarks = isSsnUnlockedFlag ? currentAppDetails.selfSsnLockedDetails?.remarks : null;

    const originalApplicationUrl = currentAppDetails.originalApplicationOpportunityUid
        ? `/insights/moreInfo?opportunityId=${currentAppDetails.originalApplicationOpportunityUid}`
        : null;

    const unlockedUserFullName = `${currentAppDetails.firstName ?? ''} ${currentAppDetails.lastName ?? ''}`.trim();

    const title = useMemo(() => {
        if (isApplicationLocked) return "Application Locked";
        if (isApplicationUnlocked) return "Application Unlocked";
        if (isSsnLocked) {
            const lockedOn = currentAppDetails.selfSsnLockedDetails?.lockedOn;
            const formattedDate = lockedOn ? formatUTCDateTime(lockedOn, false, true) : '-';
            return `Identity Locked - ${formattedDate}`;
        };
        if (isSsnUnlockedFlag) return "Identity Unlocked";
        return null;
    }, [
        isApplicationLocked,
        isApplicationUnlocked,
        isSsnLocked,
        isSsnUnlockedFlag,
        currentAppDetails.selfSsnLockedDetails?.lockedOn
    ])

    const description = useMemo(() => {
        if (isApplicationLocked) return "This application is locked because it matches an existing record.";
        if (isApplicationUnlocked) return (
            <p className={styles.unlockedDescription}>Unlocked By: <span className={styles.unlockedBy}>{unlockedBy}</span> | Unlocked At: <span className={styles.unlockedAt}>{unlockedAt}</span></p>
        );
        if (isSsnLocked) return (
            <div className={styles.identityOuterCtn}>
                {Array.from(IDENTITIES).map(identity => (
                    <div className={styles.identityCtn} key={identity}>
                        <p className={styles.identityTxt}>{identity}</p>
                    </div>
                ))}
            </div>
        );
        if (isSsnUnlockedFlag) return remarks ? `Reason: ${remarks}` : null;

        return null;
    }, [
        isApplicationLocked,
        isApplicationUnlocked,
        isSsnLocked,
        isSsnUnlockedFlag,
        unlockedBy,
        unlockedAt,
        remarks,
    ])

    const subDescription = useMemo(() => {
        if (isApplicationLocked && currentAppDetails.applicationLockedReason) return `${currentAppDetails.applicationLockedReason}`;
        if (isSsnUnlockedFlag) return (
            <p className={styles.unlockedDescription}>
                Unlocked By: <span className={styles.unlockedBy}>{unlockedBy}</span> |
                Unlocked At: <span className={styles.unlockedAt}>{unlockedAt}</span>
            </p>
        )

        return null;
    }, [
        isApplicationLocked,
        isSsnUnlockedFlag,
        unlockedBy,
        unlockedAt,
        currentAppDetails.applicationLockedReason,
    ])

    const openUnlockModal = useCallback(() => {
        setIsUnlockModalOpen(true);
    }, []);

    const closeUnlockModal = useCallback(() => {
        setIsUnlockModalOpen(false);
    }, []);

    const openSsnUnlockModal = useCallback(() => {
        setIsSsnUnlockModalOpen(true);
    }, []);

    const closeSsnUnlockModal = useCallback(() => {
        setIsSsnUnlockModalOpen(false);
    }, []);

    const handleConfirmUnlock = useCallback(async () => {
        const opportunityUid = currentAppDetails.opportunityUid;

        if(!opportunityUid) return;

        await UnLockApplication(opportunityUid);
        closeUnlockModal();
    }, [currentAppDetails.opportunityUid, closeUnlockModal, UnLockApplication]) 

    const handleConfirmSsnUnlock = useCallback(async (remarks: string | null) => {
        const opportunityUid = currentAppDetails.opportunityUid;

        if(!opportunityUid) return;

        await UnLockSsnApplication({ opportunityUid, remarks });
        closeSsnUnlockModal();
    }, [currentAppDetails.opportunityUid, closeSsnUnlockModal, UnLockSsnApplication]) 

    const handleViewBeneficiary = useCallback(() => {
        if(!beneficiaryOpportunityUrl) return;
        return globalThis.open(beneficiaryOpportunityUrl, '_blank', 'noopener,noreferrer');
    }, [beneficiaryOpportunityUrl]) 

    const handleViewOriginalApplication = useCallback(() => {
        if(!originalApplicationUrl) return;
        return globalThis.open(originalApplicationUrl, '_blank', 'noopener,noreferrer');
    }, [originalApplicationUrl]) 

    return {
        //Flags
        isApplicationLocked,
        isApplicationUnlocked,
        isSsnLocked,
        isSsnUnlocked: isSsnUnlockedFlag,
        isUnlockedThroughSsn,
        isAnyLocked,
        isAnyUnlocked,
        shouldRenderHeader,

        //Display Data
        title,
        description,
        subDescription,
        beneficiaryOpportunityUrl,
        originalApplicationUrl,
        unlockedUserFullName,
        unlockedBy, 
        unlockedAt,

        //Modal state
        isUnlockModalOpen,
        isSsnUnlockModalOpen,

        //Handlers
        openUnlockModal,
        closeUnlockModal,
        openSsnUnlockModal,
        closeSsnUnlockModal,
        handleConfirmUnlock,
        handleConfirmSsnUnlock,
        handleViewBeneficiary,
        handleViewOriginalApplication,
    };
};